/**
 * As montarias (techspec-montarias): 13 bichos do cliente da UO × (cor original + 8 tintas prime) = 117, mais as
 * 12 do Chefe do Mundo (techspec-chefe-mundo §05), só no Altar dos Fragmentos, e os 3 corcéis do Founder Pack.
 *
 * O id é `<bicho>` ou `<bicho>-<cor>`. A pasta pública é o id; o stem das folhas é o do bicho
 * (`cavalo-cinza-fogo/idle/cavalo-cinza-idle.json`), porque a arte é a mesma passada pela matiz da UO
 * (`tools/vd-para-montaria.py`). A mesma lista está em `backend/UoIdle.Api/Items/Montarias.cs`; o
 * `MontariasTests` do backend confere as duas e as folhas em `public/`.
 */

export const BICHOS = [
  'cavalo-cinza', 'cavalo-baio', 'unicornio', 'ostard-floresta', 'ostard-deserto', 'besouro',
  'dragao-pantano', 'dragao-blindado', 'cu-sidhe', 'hiryu', 'urso-polar', 'ridgeback', 'esqueleto'
] as const;

/** As 8 tintas prime, da rara para a lendária. */
export const CORES = ['sangue', 'veneno', 'neve', 'sombra', 'ouro', 'fogo', 'gelo', 'arcano'] as const;

/** Nome em português do bicho e da cor: é o que o catálogo local mostra antes do servidor responder. */
const NOME_BICHO: Readonly<Record<(typeof BICHOS)[number], string>> = {
  'cavalo-cinza': 'Cavalo Cinza',
  'cavalo-baio': 'Cavalo Baio',
  unicornio: 'Unicórnio',
  'ostard-floresta': 'Ostard da Floresta',
  'ostard-deserto': 'Ostard do Deserto',
  besouro: 'Besouro Gigante',
  'dragao-pantano': 'Dragão do Pântano',
  'dragao-blindado': 'Dragão Blindado',
  'cu-sidhe': 'Cu Sidhe',
  hiryu: 'Hiryu',
  'urso-polar': 'Urso Polar',
  ridgeback: 'Ridgeback',
  esqueleto: 'Montaria Esquelética'
};

const NOME_COR: Readonly<Record<string, string>> = {
  sangue: 'Sangue', veneno: 'Veneno', neve: 'Neve', sombra: 'Sombra',
  ouro: 'Ouro', fogo: 'Fogo', gelo: 'Gelo', arcano: 'Arcano',
  rubro: 'Rubro', brasa: 'Brasa', glacial: 'Glacial', geada: 'Geada'
};

/** As 12 do Chefe do Mundo: três bichos por cor do Balron (a mesma lista de `Montarias.DoChefe`). */
export const MONTARIAS_DO_CHEFE = [
  'cavalo-cinza-rubro', 'ridgeback-rubro', 'dragao-pantano-rubro',
  'cavalo-baio-brasa', 'besouro-brasa', 'hiryu-brasa',
  'unicornio-glacial', 'dragao-blindado-glacial', 'cu-sidhe-glacial',
  'ostard-floresta-geada', 'urso-polar-geada', 'esqueleto-geada'
] as const;

/** Os 3 corcéis do Founder Pack (techspec-correio §05): o Cavalo Cinza nos tons de cada pack, só pela carta. */
export const MONTARIAS_DO_FUNDADOR = ['cavalo-cinza-peregrino', 'cavalo-cinza-cavaleiro', 'cavalo-cinza-lorde'] as const;

const NOME_FUNDADOR: Readonly<Record<string, string>> = {
  'cavalo-cinza-peregrino': 'Corcel Peregrino',
  'cavalo-cinza-cavaleiro': 'Corcel Cavaleiro',
  'cavalo-cinza-lorde': 'Corcel Lorde'
};

function doChefe(id: string): Montaria {
  const bicho = BICHOS.find(b => id.startsWith(`${b}-`)) ?? BICHOS[0];
  const cor = id.slice(bicho.length + 1);
  return { id, bicho, cor, nome: `${NOME_BICHO[bicho]} · ${NOME_COR[cor] ?? cor}` };
}

export interface Montaria {
  id: string;
  /** O bicho: o stem das folhas. */
  bicho: string;
  cor: string | null;
  nome: string;
}

export const MONTARIAS: readonly Montaria[] = [
  ...BICHOS.flatMap(b => [
    { id: b, bicho: b, cor: null, nome: NOME_BICHO[b] },
    ...CORES.map(c => ({ id: `${b}-${c}`, bicho: b, cor: c, nome: `${NOME_BICHO[b]} · ${NOME_COR[c]}` }))
  ]),
  ...MONTARIAS_DO_CHEFE.map(doChefe),
  ...MONTARIAS_DO_FUNDADOR.map(id => ({ id, bicho: 'cavalo-cinza', cor: id.slice('cavalo-cinza-'.length), nome: NOME_FUNDADOR[id] }))
];

/** O cavalo do estábulo (era o `horse`): o que aparece quando falta arte ou não há montaria. */
export const MONTARIA_PADRAO = 'cavalo-baio';

export function iconeDaMontaria(id: string): string {
  return `/sprites/itens/animals/${id}.png`;
}
